import { useEffect, useState } from "react";
import { Linking, Platform, Pressable, StyleSheet, Text, View } from "react-native";

import {
  completePublicSignatureSession,
  getPublicSignatureDocumentUrl,
  getPublicSignatureSession,
  getSignaturePacketErrorMessage,
  PublicSignatureSession as PublicSession,
} from "../lib/central-signature-packets";
import { central, semantic, spacing } from "../theme/tokens";
import { Button, ErrorState, Field, Panel } from "./ui";

export function PublicSignatureSession({ token }: { token: string }) {
  const [session, setSession] = useState<PublicSession | null>(null);
  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(null);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void getPublicSignatureSession(token).then((result) => {
      if (!active) return;
      setSession(result);
      setTypedName("");
      setSigned(result.completed);
    }).catch((caught) => {
      if (active) setError(getSignaturePacketErrorMessage(caught));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [token]);

  const sign = async () => {
    if (!consent || !typedName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await completePublicSignatureSession(token, typedName);
      setSigned(true);
    } catch (caught) {
      setError(getSignaturePacketErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const reviewDocument = async (documentId: string) => {
    setOpeningDocumentId(documentId);
    setError(null);
    try {
      const { url } = await getPublicSignatureDocumentUrl(token, documentId);
      if (Platform.OS === "web" && typeof window !== "undefined") window.location.assign(url);
      else await Linking.openURL(url);
    } catch (caught) {
      setError(getSignaturePacketErrorMessage(caught));
    } finally {
      setOpeningDocumentId(null);
    }
  };

  return (
    <View style={styles.page}>
      <View style={styles.shell}>
        <View style={styles.brand}>
          <Text style={styles.monogram}>W</Text>
          <View>
            <Text style={styles.brandName}>WeHonor Central</Text>
            <Text style={styles.brandMeta}>Secure Signature Request</Text>
          </View>
        </View>

        {loading ? <Text accessibilityLiveRegion="polite" style={styles.muted}>Opening Secure Packet…</Text> : null}
        {error ? <ErrorState description={`${error} Ask the care team to prepare a replacement link if you still need to sign.`} title="Signature Link Unavailable" /> : null}

        {!loading && session && signed ? (
          <Panel style={styles.panel}>
            <Text accessibilityRole="header" style={styles.title}>Signature Recorded</Text>
            <Text style={styles.body}>Thank you, {session.recipientName}. Your care team has received your typed signature for {session.packetTitle}.</Text>
            <Text style={styles.muted}>You may close this page. Any required funeral home staff countersignature will be completed separately.</Text>
          </Panel>
        ) : null}

        {!loading && session && !signed ? (
          <Panel style={styles.panel}>
            <View style={styles.headerCopy}>
              <Text style={styles.eyebrow}>FOR {session.lovedOneName.toUpperCase()}</Text>
              <Text accessibilityRole="header" style={styles.title}>Review and Sign</Text>
              <Text style={styles.body}>{session.packetTitle}</Text>
            </View>
            <Text style={styles.notice}>Development signing transport: your typed signature is recorded by WeHonor for testing and is not sent through DocuSign.</Text>

            <View style={styles.section}>
              <Text accessibilityRole="header" style={styles.sectionTitle}>Forms Included</Text>
              {session.forms.map((form) => (
                <View key={form.documentId} style={styles.formRow}>
                  <View style={styles.formIdentity}>
                    <Text style={styles.check}>✓</Text>
                    <Text style={styles.formTitle}>{form.title}</Text>
                  </View>
                  <Pressable
                    accessibilityHint={`Open ${form.title} as a PDF`}
                    accessibilityRole="button"
                    disabled={openingDocumentId !== null}
                    onPress={() => void reviewDocument(form.documentId)}
                    style={styles.reviewButton}
                  >
                    <Text style={styles.reviewButtonText}>{openingDocumentId === form.documentId ? "Opening…" : "Review PDF"}</Text>
                  </Pressable>
                </View>
              ))}
            </View>

            <View style={styles.section}>
              <Text accessibilityRole="header" style={styles.sectionTitle}>Your Signature</Text>
              <Text style={styles.muted}>Signer: {session.recipientName} · {titleCase(session.recipientRole)}</Text>
              <Field
                accessibilityHint="Type the signer name exactly as shown"
                label="Typed Signature"
                onChangeText={setTypedName}
                required
                value={typedName}
              />
              <Pressable
                accessibilityRole="checkbox"
                accessibilityState={{ checked: consent }}
                onPress={() => setConsent((current) => !current)}
                style={styles.consentRow}
              >
                <Text style={styles.checkbox}>{consent ? "✓" : ""}</Text>
                <Text style={styles.consentText}>I agree to use this typed name as my signature and confirm that I reviewed the forms listed above.</Text>
              </Pressable>
              <Text style={styles.muted}>This records typed-name consent and workflow evidence. It does not independently verify identity or legal authority.</Text>
            </View>

            <Button
              disabled={!consent || !typedName.trim()}
              fullWidth
              label="Sign Packet"
              loading={busy}
              onPress={() => void sign()}
              shape="rounded"
            />
            <Text style={styles.expiry}>Link expires {formatDateTime(session.expiresAt)}</Text>
          </Panel>
        ) : null}
      </View>
    </View>
  );
}

function titleCase(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

const styles = StyleSheet.create({
  body: { color: central.text.secondary, fontFamily: "Manrope_400Regular", fontSize: 15, lineHeight: 23 },
  brand: { alignItems: "center", flexDirection: "row", gap: spacing.sm },
  brandMeta: { color: central.text.muted, fontFamily: "Manrope_500Medium", fontSize: 11 },
  brandName: { color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 16 },
  check: { color: semantic.status.success.foreground, fontFamily: "Manrope_700Bold", fontSize: 14 },
  checkbox: { borderColor: central.border.strong, borderRadius: 4, borderWidth: 1, color: central.action.primary, fontFamily: "Manrope_700Bold", fontSize: 14, height: 22, lineHeight: 20, textAlign: "center", width: 22 },
  consentRow: { alignItems: "flex-start", flexDirection: "row", gap: spacing.sm, minHeight: 44 },
  consentText: { color: central.text.primary, flex: 1, fontFamily: "Manrope_500Medium", fontSize: 13, lineHeight: 20 },
  expiry: { color: central.text.muted, fontFamily: "Manrope_500Medium", fontSize: 11, textAlign: "center" },
  eyebrow: { color: central.text.muted, fontFamily: "Manrope_700Bold", fontSize: 11, letterSpacing: 1 },
  formIdentity: { alignItems: "center", flex: 1, flexDirection: "row", gap: spacing.sm },
  formRow: { alignItems: "center", borderBottomColor: central.border.subtle, borderBottomWidth: 1, flexDirection: "row", gap: spacing.sm, justifyContent: "space-between", paddingVertical: spacing.sm },
  formTitle: { color: central.text.primary, flex: 1, fontFamily: "Manrope_600SemiBold", fontSize: 14 },
  headerCopy: { gap: spacing.xs },
  monogram: { backgroundColor: central.action.primary, borderRadius: central.radius.control, color: central.text.inverse, fontFamily: "Manrope_700Bold", fontSize: 18, height: 42, lineHeight: 42, overflow: "hidden", textAlign: "center", width: 42 },
  muted: { color: central.text.muted, fontFamily: "Manrope_400Regular", fontSize: 12, lineHeight: 18 },
  notice: { backgroundColor: semantic.status.warning.background, borderRadius: central.radius.control, color: semantic.status.warning.foreground, fontFamily: "Manrope_500Medium", fontSize: 12, lineHeight: 18, padding: spacing.md },
  page: { alignItems: "center", backgroundColor: central.surface.canvas, flex: 1, minHeight: "100%", padding: spacing.md },
  panel: { gap: spacing.lg, width: "100%" },
  section: { gap: spacing.sm },
  sectionTitle: { color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 15 },
  reviewButton: { alignItems: "center", borderColor: central.border.strong, borderRadius: central.radius.control, borderWidth: 1, justifyContent: "center", minHeight: 44, paddingHorizontal: spacing.sm },
  reviewButtonText: { color: central.action.primary, fontFamily: "Manrope_700Bold", fontSize: 12 },
  shell: { gap: spacing.lg, maxWidth: 620, paddingBottom: spacing.xxl, paddingTop: spacing.lg, width: "100%" },
  title: { color: central.text.primary, fontFamily: "Manrope_700Bold", fontSize: 30, letterSpacing: -0.8 },
});
